import React from "react";
import { Link } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import {
  HeroSection,
  LeftPanel,
  LeftHeading,
  LeftBody,
  ButtonGroup,
  GhostButton,
  RightPanel,
  RightTopHeading,
  RightSubheading,
  StatsList,
  StatItem,
  StatNumber,
  StatText,
  VisionSection,
  VisionInner,
  VisionLeft,
  VisionLabel,
  VisionHeadline,
  VisionBody,
  VisionCallout,
  VisionCalloutText,
  WhatWeDoSection,
  WhatWeDoInner,
  WhatWeDoLabel,
  WhatWeDoSubtitle,
  WhatWeDoGrid,
  WhatWeDoCard,
  WhatWeDoCardAccent,
  WhatWeDoCardBody,
  WhatWeDoCardHeading,
  WhatWeDoCardText,
  WhySection,
  WhyInner,
  WhyLeft,
  WhyLabel,
  WhyBody,
  WhyCard,
  WhyCardHeading,
  WhyCheckList,
  WhyCheckItem,
  WhyCheckIcon,
  GradientDivider,
  MissionBand,
  MissionLabel,
  MissionText,
  ExploreSection,
  ExploreInner,
  ExploreHeading,
  ExploreSubtitle,
  ExploreGrid,
  ExploreCard,
  ExploreCardTitle,
  ExploreCardText,
  siteLightBlue,
  siteYellow,
  sitePurple
} from "./styled.components";

const stats = [
  {
    number: "1 in 13",
    color: siteLightBlue,
    text: (
      <>
        Children in the U.S. has a food allergy — that&apos;s roughly{" "}
        <strong>2 students in every classroom</strong>
      </>
    )
  },
  {
    number: "50%",
    color: siteYellow,
    text: "Increase in childhood food allergies from 2007 to 2021"
  },
  {
    number: "25%",
    color: sitePurple,
    text: (
      <>
        Of severe school reactions happen in children with{" "}
        <strong>no prior allergy diagnosis</strong>
      </>
    )
  }
];

const AboutUs: React.FC = () => {
  return (
    <>
      <HeroSection>
        <LeftPanel>
          <LeftHeading>
            Education.
            <br />
            Preparedness.
            <br />
            Saving Lives.
          </LeftHeading>
          <LeftBody>
            We are a nonprofit built on a mother&apos;s love and a mission that
            could not wait. KRF exists to make sure every school, every
            community, and every family has the knowledge and tools to respond
            when it matters most.
          </LeftBody>
          <ButtonGroup>
            <GhostButton
              to="/about-us/kyahs-story"
              onClick={() => window.scrollTo(0, 0)}
            >
              Kyah&apos;s Story
            </GhostButton>
            <GhostButton to="/trainings" onClick={() => window.scrollTo(0, 0)}>
              Explore Trainings
            </GhostButton>
          </ButtonGroup>
        </LeftPanel>

        <RightPanel>
          <RightTopHeading>Why Food Allergy Readiness Matters</RightTopHeading>
          <RightSubheading>
            Food allergy emergencies can happen anywhere, including to children
            with no prior diagnosis.
          </RightSubheading>
          <StatsList>
            {stats.map((stat, i) => (
              <StatItem key={i}>
                <StatNumber $color={stat.color}>{stat.number}</StatNumber>
                <StatText>{stat.text}</StatText>
              </StatItem>
            ))}
          </StatsList>
        </RightPanel>
      </HeroSection>

      <MissionBand>
        <MissionLabel>OUR MISSION</MissionLabel>
        <MissionText>
          The Kyah Rayne Foundation is dedicated to advancing food allergy
          awareness and education while increasing access to life-saving
          epinephrine.
        </MissionText>
      </MissionBand>

      <VisionSection>
        <VisionInner>
          <VisionLeft>
            <VisionLabel>Our Vision</VisionLabel>
            <VisionHeadline>
              No one else should have to die from a food allergy emergency.
            </VisionHeadline>
            <VisionBody>
              We envision a world where schools, early childhood programs,
              restaurants, and community spaces are equipped to prevent and
              respond to food allergy emergencies with confidence.
            </VisionBody>
          </VisionLeft>
          <VisionCallout>
            <VisionCalloutText>
              Prepared communities save lives before an emergency becomes a
              tragedy.
            </VisionCalloutText>
          </VisionCallout>
        </VisionInner>
      </VisionSection>

      <GradientDivider />

      <WhySection>
        <WhyInner>
          <WhyLeft>
            <WhyLabel>Our Why</WhyLabel>
            <WhyBody>
              Kyah&apos;s story is the heart of our work. What began as a
              family&apos;s loss has grown into a movement to make food allergy
              education more accessible, practical, and lifesaving.
            </WhyBody>
            <WhyBody>
              Through free training, outreach, advocacy, and community
              partnerships, KRF helps close the gap between awareness and
              emergency readiness.
            </WhyBody>
          </WhyLeft>
          <WhyCard>
            <WhyCardHeading>KRF turns awareness into action.</WhyCardHeading>
            <WhyCheckList>
              {[
                "Free education for schools, food service teams, and community partners.",
                "Practical tools that support emergency recognition and response.",
                "Partnerships that expand food allergy readiness across communities."
              ].map((item, i) => (
                <WhyCheckItem key={i}>
                  <WhyCheckIcon>
                    <FaCheckCircle color="#40B75E" size="1.3rem" />
                  </WhyCheckIcon>
                  {item}
                </WhyCheckItem>
              ))}
            </WhyCheckList>
          </WhyCard>
        </WhyInner>
      </WhySection>

      <WhatWeDoSection>
        <WhatWeDoInner>
          <WhatWeDoLabel>What We Do</WhatWeDoLabel>
          <WhatWeDoSubtitle>
            KRF supports food allergy readiness through education, outreach,
            advocacy, and community-centered partnerships.
          </WhatWeDoSubtitle>
          <WhatWeDoGrid>
            {[
              {
                heading: "Train Schools",
                body: "Free food allergy education for school personnel, with a focus on emergency recognition, epinephrine readiness, and student safety."
              },
              {
                heading: "Support Food Service",
                body: "Training and tools that help restaurants, hotels, and food service teams create safer, more inclusive experiences."
              },
              {
                heading: "Build Community Readiness",
                body: "Outreach, advocacy, toolkits, and partnerships that help communities prepare before an emergency happens."
              },
              {
                heading: "Develop Public Health Leaders",
                body: "Internship opportunities that give students hands-on experience in health education, outreach, policy, and nonprofit programs."
              }
            ].map((card, i) => (
              <WhatWeDoCard key={i}>
                <WhatWeDoCardAccent />
                <WhatWeDoCardBody>
                  <WhatWeDoCardHeading>{card.heading}</WhatWeDoCardHeading>
                  <WhatWeDoCardText>{card.body}</WhatWeDoCardText>
                </WhatWeDoCardBody>
              </WhatWeDoCard>
            ))}
          </WhatWeDoGrid>
        </WhatWeDoInner>
      </WhatWeDoSection>

      <ExploreSection>
        <ExploreInner>
          <ExploreHeading>Explore More About KRF</ExploreHeading>
          <ExploreSubtitle>
            Learn about our story, our leadership, our partners, and our work in
            the community.
          </ExploreSubtitle>
          <ExploreGrid>
            {[
              {
                title: "Kyah's Story",
                body: "Learn about Kyah, her legacy, and the story behind our mission.",
                to: "/about-us/kyahs-story"
              },
              {
                title: "Our Team",
                body: "Meet the leaders, advisors, and community advocates guiding KRF's mission and growth.",
                to: "/about-us/our-team"
              },
              {
                title: "Collaborators",
                body: "Explore the partners, organizations, and community leaders helping expand food allergy readiness.",
                to: "/about-us/collaborators"
              },
              {
                title: "In The News",
                body: "Read features, articles, media mentions, and community updates from KRF.",
                to: "/about-us/in-the-news"
              }
            ].map((card, i) => (
              <ExploreCard key={i}>
                <ExploreCardTitle
                  as={Link}
                  to={card.to}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  {card.title}
                </ExploreCardTitle>
                <ExploreCardText>{card.body}</ExploreCardText>
              </ExploreCard>
            ))}
          </ExploreGrid>
        </ExploreInner>
      </ExploreSection>
    </>
  );
};

export default AboutUs;
